import { useDataStore } from "@/stores/data";

/**
 * RequestManager
 * - One timeout per request
 * - Next timeout delay = intervalMs - lastDurationMs
 * - If delay <= 0 → schedule full interval and tag request as lagging
 * - Exposes getStatuses() so UI can read lag, lastDuration, nextRunAt, etc.
 */
class RequestManager {
  constructor() {
    /**
     * tasks[key] = {
     *   timer: number|null,
     *   intervalMs: number,
     *   req: { type, id, intervalSec },
     *   context: Vue instance,
     *   status: {
     *     lag: boolean,
     *     lastDurationMs: number|null,
     *     lastRunAt: number|null,
     *     nextRunAt: number|null,
     *     consecutiveLags: number,
     *     lastError: string|null
     *   }
     * }
     */
    this.tasks = {};
  }

  _key(req) {
    return `${req.type}:${req.id || "self"}`;
  }

  stopAll() {
    Object.values(this.tasks).forEach((t) => {
      if (t.timer) clearTimeout(t.timer);
    });
    this.tasks = {};
  }

  /**
   * requests: Array<{ type: 'user'|'variable'|'device'|'dashboard', id?: number, intervalSec: number }>
   * context: Vue component instance (for $session and $dataService)
   */
  startRequests(requests, context) {
    this.stopAll();

    requests.forEach((req) => {
      const intervalMs = Math.max(1, Number(req.intervalSec || 1)) * 1000;
      const key = this._key(req);

      this.tasks[key] = {
        timer: null,
        intervalMs,
        req,
        context,
        status: {
          lag: false,
          lastDurationMs: null,
          lastRunAt: null,
          nextRunAt: null,
          consecutiveLags: 0,
          lastError: null,
        },
      };

      // first run now
      this._runOnce(key);
    });
  }

  async _runOnce(key) {
    const task = this.tasks[key];
    if (!task) return;

    const reqStore = useDataStore();
    const { req, context } = task;
    const startedAt = Date.now();
    task.status.lastError = null;

    try {
      const dataSvc = new context.$dataService();
      let res = null;

      if (req.type === "user") {
        const userId = context.$session.get("logged_id");
        res = await dataSvc.filterByUserId(userId);
      } else if (req.type === "variable" && req.id) {
        res = await dataSvc.filterByVariableId(req.id);
      } else if (req.type === "device" && req.id) {
        res = await dataSvc.filterByDeviceId(req.id);
      } else if (req.type === "dashboard" && req.id) {
        res = await dataSvc.filterByDashboardId(req.id);
      }

      //console.log(res);
      if (res) {
        const items = Array.isArray(res) ? res : [res];
        reqStore.updateOrMerge(items);
      }
    } catch (e) {
      task.status.lastError = e?.message || String(e);
      // (optional) you can toast here
    } finally {
      const finishedAt = Date.now();
      const duration = finishedAt - startedAt;
      task.status.lastDurationMs = duration;
      task.status.lastRunAt = finishedAt;

      // compute next delay = interval - duration
      let nextDelay = task.intervalMs - duration;

      if (nextDelay <= 0) {
        // too slow: schedule full interval and tag as lagging
        nextDelay = task.intervalMs;
        task.status.lag = true;
        task.status.consecutiveLags += 1;
      } else {
        task.status.lag = false;
        task.status.consecutiveLags = 0;
      }

      const nextRunAt = finishedAt + nextDelay;
      task.status.nextRunAt = nextRunAt;

      // schedule next run
      if (task.timer) clearTimeout(task.timer);
      task.timer = setTimeout(() => this._runOnce(key), nextDelay);
    }
  }

  /**
   * Update a single request's interval on the fly.
   */
  updateInterval(reqIdentity, newIntervalSec) {
    const key = this._key(reqIdentity);
    const t = this.tasks[key];
    if (!t) return;

    t.intervalMs = Math.max(1, Number(newIntervalSec || 1)) * 1000;

    // recompute nextRunAt based on now + new interval
    if (t.timer) clearTimeout(t.timer);
    const delay = t.intervalMs;
    t.status.nextRunAt = Date.now() + delay;
    t.timer = setTimeout(() => this._runOnce(key), delay);
  }

  /**
   * Stop a single request.
   */
  stopRequest(reqIdentity) {
    const key = this._key(reqIdentity);
    const t = this.tasks[key];
    if (!t) return;
    if (t.timer) clearTimeout(t.timer);
    delete this.tasks[key];
  }

  /**
   * Read-only snapshot of statuses for UI.
   */
  getStatuses() {
    const out = {};
    Object.entries(this.tasks).forEach(([key, t]) => {
      out[key] = {
        ...t.status,
        intervalMs: t.intervalMs,
        type: t.req.type,
        id: t.req.id || null,
      };
    });
    return out;
  }
}

export const requestManager = new RequestManager();
